<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AssistantForm from './AssistantForm.vue';

const props = defineProps({
  selectedAssistant: {
    type: Object,
    default: () => ({}),
  },
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
});
const emit = defineEmits(['close', 'created']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const assistantForm = ref(null);
const discardDialog = ref(null);
const saving = ref(false);
const saveError = ref('');
const { currentAccount } = useAccount();

const updateAssistant = assistantDetails =>
  store.dispatch('captainAssistants/update', {
    id: props.selectedAssistant.id,
    ...assistantDetails,
  });

const i18nKey = computed(
  () => `CAPTAIN.ASSISTANTS.${props.type.toUpperCase()}`
);

const handleSubmit = async updatedAssistant => {
  if (saving.value) return;
  const accountId = currentAccount.value?.id;
  const assistantId = props.selectedAssistant?.id;
  const sameContext = () =>
    accountId === currentAccount.value?.id &&
    assistantId === props.selectedAssistant?.id;
  saving.value = true;
  saveError.value = '';
  try {
    const result =
      props.type === 'edit'
        ? await updateAssistant(updatedAssistant)
        : await store.dispatch('captainAssistants/create', updatedAssistant);
    if (!sameContext()) return;
    useAlert(t(`${i18nKey.value}.SUCCESS_MESSAGE`));
    dialogRef.value.close();
    if (props.type === 'create') emit('created', result);
  } catch (error) {
    if (sameContext())
      saveError.value = error?.message || t(`${i18nKey.value}.ERROR_MESSAGE`);
  } finally {
    saving.value = false;
  }
};

const handleClose = () => {
  saveError.value = '';
  emit('close');
};

const handleCancel = () => {
  if (saving.value) return;
  if (assistantForm.value?.isDirty) {
    discardDialog.value.open();
    return;
  }
  dialogRef.value.close();
};
const discardChanges = () => {
  discardDialog.value.close();
  dialogRef.value.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t(`${i18nKey}.TITLE`)"
    :description="t('CAPTAIN.ASSISTANTS.FORM_DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    confirm-before-close
    overflow-y-auto
    @confirm="assistantForm?.submit()"
    @request-close="handleCancel"
    @close="handleClose"
  >
    <p
      v-if="saveError"
      role="alert"
      class="mb-4 rounded-xl border border-n-weak p-3 text-sm text-n-ruby-11"
    >
      {{ saveError }}
    </p>
    <AssistantForm
      ref="assistantForm"
      root-tag="div"
      :mode="type"
      :saving="saving"
      :assistant="selectedAssistant"
      @submit="handleSubmit"
      @cancel="handleCancel"
    />
    <template #footer />
  </Dialog>
  <Dialog
    ref="discardDialog"
    type="alert"
    :title="t('CAPTAIN.ASSISTANTS.DRAFT.TITLE')"
    :description="t('CAPTAIN.ASSISTANTS.DRAFT.DESCRIPTION')"
    :confirm-button-label="t('CAPTAIN.ASSISTANTS.DRAFT.DISCARD')"
    :cancel-button-label="t('CAPTAIN.ASSISTANTS.DRAFT.KEEP')"
    @confirm="discardChanges"
  />
</template>
